import { useApolloClient } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  PartnerSetting,
  useManagePartnerSettingsValue,
  usePartnerSettings,
} from 'src/graphql/entities/partner-setting';
import {
  computeNextDisabledCsv,
  parseDisabledIds,
} from 'src/pages/Settings/users/assignee-groups/utils/disabled-ids';
import { useNotifications } from 'src/utils/hooks';

interface ToggleArgs {
  conversationAssigneeGroupId: string;
  shouldBeEnabled: boolean;
}

export function usePartnerDisabledAssigneeGroupIds(partnerId: number | null) {
  const { data, loading } = usePartnerSettings({
    partnerSetting: PartnerSetting.DisabledConversationAssigneeGroups,
    partnerId,
  });

  const partnerRecord = useMemo(() => {
    if (!Array.isArray(data)) return null;
    return data.find((r) => r.partnerId === partnerId) ?? null;
  }, [data, partnerId]);

  const disabledIds = useMemo(
    () => new Set(parseDisabledIds(partnerRecord?.value)),
    [partnerRecord?.value],
  );

  return {
    disabledIds,
    partnerRecord,
    loading,
  };
}

export function useToggleAssigneeGroupEnabled(partnerId: number | null) {
  const { showErrorAlert } = useNotifications();
  const apollo = useApolloClient();
  const { partnerRecord, loading: settingLoading } =
    usePartnerDisabledAssigneeGroupIds(partnerId);
  const { createPartnerSettingsValue, updatePartnerSettingsValue } =
    useManagePartnerSettingsValue(partnerId);

  const latestDisabledCsvRef = useRef<string | undefined>(partnerRecord?.value);
  const inFlightCountRef = useRef(0);

  useEffect(() => {
    if (inFlightCountRef.current === 0) {
      latestDisabledCsvRef.current = partnerRecord?.value;
    }
  }, [partnerRecord?.value]);

  const toggle = useCallback(
    async ({ conversationAssigneeGroupId, shouldBeEnabled }: ToggleArgs) => {
      if (partnerId === null) return;

      const nextValue = computeNextDisabledCsv(
        latestDisabledCsvRef.current,
        conversationAssigneeGroupId,
        shouldBeEnabled,
      );
      latestDisabledCsvRef.current = nextValue;
      inFlightCountRef.current += 1;

      try {
        if (partnerRecord?.partnerSettingValueId) {
          await updatePartnerSettingsValue({
            partnerSettingValueId: partnerRecord.partnerSettingValueId,
            partnerSettingId: PartnerSetting.DisabledConversationAssigneeGroups,
            value: nextValue,
          });
        } else {
          await createPartnerSettingsValue({
            partnerSettingId: PartnerSetting.DisabledConversationAssigneeGroups,
            value: nextValue,
          });
        }
        apollo.cache.evict({ fieldName: 'getEnabledConversationAssigneeGroups' });
        apollo.cache.gc();
      } catch (err) {
        latestDisabledCsvRef.current = computeNextDisabledCsv(
          latestDisabledCsvRef.current,
          conversationAssigneeGroupId,
          !shouldBeEnabled,
        );
        showErrorAlert(
          err instanceof Error
            ? err.message
            : 'Failed to update assignee group state. Please try again.',
        );
        throw err;
      } finally {
        inFlightCountRef.current -= 1;
      }
    },
    [
      apollo,
      createPartnerSettingsValue,
      partnerId,
      partnerRecord,
      showErrorAlert,
      updatePartnerSettingsValue,
    ],
  );

  return {
    toggleAssigneeGroupEnabled: toggle,
    settingLoading,
  };
}
